import React from "react";
import { Edit2, Trash2, ArrowUpDown, BarChart3, ArrowUp } from "lucide-react";
import type { Campaign } from "../../types/campaign";
import type { City } from "../../types/city";

interface CampaignsTableProps {
  campaigns: Campaign[];
  cities: City[];
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (column: string) => void;
  onEdit: (campaign: Campaign) => void;
  onDelete: (campaign: Campaign) => void;
  onCalculateImpressions: (campaign: Campaign) => void;
}

// Helper function to get campaign status
const getCampaignStatus = (
  campaign: Campaign,
): "active" | "upcoming" | "ended" => {
  const today = new Date().toISOString().split("T")[0];
  if (today >= campaign.start_date && today <= campaign.end_date)
    return "active";
  if (today < campaign.start_date) return "upcoming";
  return "ended";
};

// Helper function to get status badge color
const getStatusBadgeColor = (
  status: "active" | "upcoming" | "ended",
): string => {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";
    case "upcoming":
      return "bg-blue-100 text-blue-800";
    case "ended":
      return "bg-gray-100 text-gray-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const CampaignsTable: React.FC<CampaignsTableProps> = ({
  campaigns,
  cities,
  sortBy,
  sortOrder,
  onSort,
  onEdit,
  onDelete,
  onCalculateImpressions,
}) => {
  const getSortIcon = (column: string) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return (
      <ArrowUp
        className={`w-4 h-4 text-primary-600 ${
          sortOrder === "desc" ? "rotate-180" : ""
        }`}
      />
    );
  };

  const getCityChips = (cityIdsStr: string) => {
    const cityIds = cityIdsStr
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);

    return cityIds.map((cityId) => {
      const city = cities.find((c) => c.city_id === cityId);
      return (
        <span
          key={cityId}
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mr-1 mb-1"
        >
          {city ? city.city_name : cityId}
        </span>
      );
    });
  };

  if (campaigns.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <p className="text-gray-500">No campaigns found</p>
        <p className="text-sm text-gray-400 mt-1">
          Try adjusting your filters or create a new campaign
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => onSort("campaign_id")}
              >
                <div className="flex items-center space-x-1">
                  <span>Campaign ID</span>
                  {getSortIcon("campaign_id")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => onSort("start_date")}
              >
                <div className="flex items-center space-x-1">
                  <span>Start Date</span>
                  {getSortIcon("start_date")}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => onSort("end_date")}
              >
                <div className="flex items-center space-x-1">
                  <span>End Date</span>
                  {getSortIcon("end_date")}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Bikes
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cities
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {campaigns.map((campaign) => {
              const status = getCampaignStatus(campaign);
              const bikeCount = campaign.bike_ids
                .split(",")
                .filter((id) => id.trim()).length;

              return (
                <tr key={campaign.campaign_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {campaign.campaign_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {campaign.start_date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {campaign.end_date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {bikeCount} {bikeCount === 1 ? "bike" : "bikes"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div className="flex flex-wrap gap-1">
                      {getCityChips(campaign.city_ids)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(status)}`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => onCalculateImpressions(campaign)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                        title="Calculate impressions"
                      >
                        <BarChart3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onEdit(campaign)}
                        className="text-primary-600 hover:text-primary-900 p-1 rounded hover:bg-primary-50"
                        title="Edit campaign"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => onDelete(campaign)}
                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                        title="Delete campaign"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
